import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FiMoreHorizontal } from "react-icons/fi";
import SkillCard from "../../components/Common/CommonCard";
import AgentCardSkeleton from "../../components/Common/AgentCardSkeleton";
import ReusableTable from "../../components/Common/BaseTable";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RefreshCw, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import Breadcrumb from "../../components/Common/Bredcrump";
import DocumentModal from "../../components/ProductDescription/DocumentModal";
import StartAutomationModal from "../Automation/StartAutomationModal";
import DownloadFormatModal from "../../components/Common/DownloadFormatModal";
import DeleteConfirmationModal from "../../components/Common/DeleteConfirmationModal";
import ActionMenu from "../../components/ProductDescription/ActionMenu";
import talkingToAIIcon from "../../assets/talkingToAI.png";
import axiosInstance from "../../utils/api";
import { updateInvoiceProcessing, getInvoiceProcessing } from "../../services/playgroundService";
import { executeManualAgent } from "../../utils/Product";
import { useStreamingJson } from "../../utils/useStreamingJson";
import { generateSessionId } from "../../utils/session";
import {
  tryParseFourayRawBuffer,
} from "../../utils/runAgentResponse";
import { agentHasUploadEnabled } from "../../utils/agentVariables";
import {
  isApAgent,
  idsMatch,
  resolveAutomationAgentId,
  resolveAutomationQuery,
  resolveAgentQuery,
  buildManualAgentStreamOptions,
  MANUAL_RUN_FILTERS,
  MANUAL_RUN_METADATA,
} from "../Automation/automationAgentConfig";
import {
  humanizeOutputColumnKey,
  mapOutputToDynamicTableRow,
  isAutomationOutputProcessing,
  capitalizeFirstLetter,
} from "../Automation/automationOutputTableUtils";
import {
  fetchAutomationOutputs,
  deleteAutomationOutput,
  resolveOutputTableNameFromAutomation,
} from "../../services/automationOutputService";
import {
  fetchAutomations,
  fetchAutomationById,
} from "../../services/automationService";

const STATUS_CONSTANTS = {
  PROCESSING: "PROCESSING",
  COMPLETED: "Completed",
  REVIEW_EXCEPTIONS: "Review Exceptions",
  FAILED: "Failed",
  PENDING: "Pending",  
};

const STATUS_VALUES = {
  SUCCESS: "success",
  COMPLETED: "completed",
  FAILED: "failed",
  ERROR: "error",
  PENDING: "pending",
  PROCESSING: "processing",
  CHECKING_INVOICE: "checking_invoice",
};

const PROCESSING_STATUSES = ["checking_invoice", "processing", "analysing_invoice", "start"];
const FAILED_STATUSES = ["failed", "error"];

const EVENT_TYPES = {
  START: "start",
  STARTED: "started",
  UPDATE: "update",
  COMPLETE: "complete",
  COMPLETED: "completed",
  FINISH: "finish",
  FINISHED: "finished",
  PROCESSING: "processing",
  ANALYSING_INVOICE: "analysing_invoice",
  INVOICE_PROCESSINGS: "InvoiceProcessings", // API sends array of invoice_processings
};

const STATUS_COLORS = {
  PROCESSING: "#1976d2",
  PROCESSING_BG: "#e3f2fd",
  COMPLETED: "green",
  REVIEW_EXCEPTIONS: "orange",
  FAILED: "red",
};

const DEFAULT_SOURCE = "Invoice Processing";
const PROCESSING_TEXT = "Processing...";
const RUN_AGENT_PLACEHOLDER_ID = "__run_agent_placeholder__";
const OUTPUT_POLL_INTERVAL_MS = 5000;
const EMPTY_ARRAY = [];

/** Ensure fouray_response is stored as { response: ... } matching the API shape. */
const wrapFourayResponse = (data) => {
  if (data == null || data === "") {
    return { response: {} };
  }

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) return { response: {} };
    try {
      return wrapFourayResponse(JSON.parse(trimmed));
    } catch {
      return { response: trimmed };
    }
  }

  if (typeof data === "object" && !Array.isArray(data)) {
    if (Object.prototype.hasOwnProperty.call(data, "response")) {
      return data;
    }
    return { response: data };
  }

  return { response: data };
};

/** Deep merge objects; later values override. Used to bind all streamed JSON for one invoice. */
const deepMerge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();
  if (source && typeof source === "object" && !Array.isArray(source)) {
    for (const key of Object.keys(source)) {
      if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
        if (!target[key] || typeof target[key] !== "object") target[key] = {};
        deepMerge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  return sources.length ? deepMerge(target, ...sources) : target;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

/** Human-readable labels for stream/API status values */
const STATUS_DISPLAY_LABELS = {
  start: "Started",
  processing: "Processing",
  analysing_invoice: "Analysing invoice",
  checking_invoice: "Checking invoice",
  update: "Updating",
  complete: "Complete",
  completed: "Completed",
  pending: "Pending",
  failed: "Failed",
  error: "Failed",
};

const formatRunStatus = (status, runStatus, isProcessing = false) => {
  const currentStatus = (status || "").toLowerCase();
  const currentRunStatus = (runStatus || "").toLowerCase();

  // API sends status "pending" + run_status "processing" → show "Processing"
  if (currentRunStatus === "processing") {
    return "Processing";
  }

  // Explicit display label from response (so "Processing", "Analysing invoice", etc. show as-is)
  if (STATUS_DISPLAY_LABELS[currentStatus]) {
    const label = STATUS_DISPLAY_LABELS[currentStatus];
    if (PROCESSING_STATUSES.includes(currentStatus)) {
      return label; // e.g. "Processing", "Analysing invoice"
    }
    if (currentStatus === "complete" || currentStatus === "completed") {
      return currentRunStatus === STATUS_VALUES.SUCCESS ? STATUS_CONSTANTS.COMPLETED : label;
    }
    if (FAILED_STATUSES.includes(currentStatus)) {
      return STATUS_CONSTANTS.FAILED;
    }
    if (currentStatus === "pending") return STATUS_CONSTANTS.PENDING;
    return label;
  }

  // Processing states - show spinner and label
  if (isProcessing || PROCESSING_STATUSES.includes(currentStatus)) {
    return STATUS_DISPLAY_LABELS[currentStatus] || STATUS_CONSTANTS.PROCESSING;
  }

  // Completed with success
  if (currentStatus === STATUS_VALUES.COMPLETED && currentRunStatus === STATUS_VALUES.SUCCESS) {
    return STATUS_CONSTANTS.COMPLETED;
  }

  // Completed without success run_status - needs review
  if (currentStatus === STATUS_VALUES.COMPLETED && currentRunStatus !== STATUS_VALUES.SUCCESS) {
    return STATUS_CONSTANTS.REVIEW_EXCEPTIONS;
  }

  // Failed states
  if (FAILED_STATUSES.includes(currentStatus) || FAILED_STATUSES.includes(currentRunStatus)) {
    return STATUS_CONSTANTS.FAILED;
  }

  // Pending state
  if (currentStatus === STATUS_VALUES.PENDING) {
    return STATUS_CONSTANTS.PENDING;
  }

  return capitalizeFirstLetter(status || STATUS_CONSTANTS.PENDING);
};

const TableActionsCell = ({ row, rowKey, selectedRowKey, onToggle, onMenuAction }) => {
  const anchorRef = useRef(null);
  const isOpen = selectedRowKey === rowKey;

  return (
    <div ref={anchorRef} className="table-actions-cell">
      <FiMoreHorizontal
        className="three-dot-menu"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(rowKey);
        }}
        style={{ cursor: "pointer" }}
      />
      {isOpen && (
        <ActionMenu
          anchorRef={anchorRef}
          onAction={(action) => onMenuAction(action, row)}
        />
      )}
    </div>
  );
};

const Skill = () => {
  const navigate = useNavigate();
  const { agentName } = useParams();
  const [searchParams] = useSearchParams();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStartRunModal, setShowStartRunModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDownloadRows, setPendingDownloadRows] = useState([]);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchColumn, setSearchColumn] = useState(null);
  const [downloadSelectAll, setDownloadSelectAll] = useState(false);
  const invoiceProcessingsAbortRef = useRef(null);
  /** Accumulate streamed payloads per invoice id; when a file completes we merge and call save API */
  const streamedPayloadsByInvoiceIdRef = useRef({});
  /** Avoid saving the same invoice twice when both "complete" and "completed" events fire */
  const savedInvoiceIdsRef = useRef(new Set());
  const activeUpdatesRef = useRef(new Set());
  const [runningAutomationId, setRunningAutomationId] = useState(null);
  const [runningAgentId, setRunningAgentId] = useState(null);
  const { run: runStreaming } = useStreamingJson();

  // Agents data from API
  const [agents, setAgents] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [loadingAutomations, setLoadingAutomations] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    size: 14,
    total_pages: 1,
    total_count: 0,
  });

  const [allData, setAllData] = useState([]);
  const [outputListColumns, setOutputListColumns] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current_page: 1,
    per_page: 5,
    total_pages: 1,
    total_count: 0,
  });

  // Debounce search so we don't spam the API while typing
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const isRunPlaceholderRow = useCallback(
    (row) => row?.id === RUN_AGENT_PLACEHOLDER_ID || row?.isRunAgentPlaceholder,
    []
  );

  const removeRunPlaceholderRows = useCallback(
    (rows) => (rows || []).filter((row) => !isRunPlaceholderRow(row)),
    [isRunPlaceholderRow]
  );

  const renderLoadingSkeleton = (width = "80%") => (
    <div
      style={{
        width,
        height: "12px",
        borderRadius: "6px",
        background: "linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)",
        backgroundSize: "400% 100%",
        animation: "shimmer 1.4s ease infinite",
      }}
    />
  );

  const formatTableCellText = useCallback((value) => {
    if (value == null || value === "") return "—";
    return capitalizeFirstLetter(String(value));
  }, []);

  const normalizeAgentName = useCallback((name) => {
    if (!name) return "";
    return name
      .toLowerCase()
      .trim()
      .replace(/[-_\s]+/g, " ")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, []);

  const fetchAgents = useCallback(async (page = 1, size = 14) => {
    setLoadingAgents(true);
    try {
      const response = await axiosInstance.get('/api/v1/diy_agents', {
        params: {
          page,
          size,
          is_publish: true,
        },
      });

      if (response.data?.success) {
        const mappedAgents = response.data.data.map((agent) => ({
          id: agent.id,
          name: agent.name,
          icon: talkingToAIIcon,
          description: agent.description || agent.agent_goal || '',
          route: `/agents/${agent.id}`,
          fullData: agent,
        }));

        setAgents(mappedAgents);

        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page,
            size: response.data.pagination.size,
            total_pages: response.data.pagination.total_pages,
            total_count: response.data.pagination.total_count,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents(pagination.page, pagination.size);
  }, [pagination.page, pagination.size, fetchAgents]);

  const fetchAutomationsList = useCallback(async () => {
    setLoadingAutomations(true);
    try {
      const params = { page: 1, per_page: 20 };
      if (selectedAgent?.id) {
        params.diy_agent_id = selectedAgent.id;
      }
      const { automations: list } = await fetchAutomations(params);
      setAutomations(list);
    } catch (error) {
      console.error("Error fetching automations:", error);
      setAutomations([]);
    } finally {
      setLoadingAutomations(false);
    }
  }, [selectedAgent?.id]);

  useEffect(() => {
    fetchAutomationsList();
  }, [fetchAutomationsList]);

  const isProcessingStatus = useCallback((status, runStatus) => {
    const runStatusLower = (runStatus || "").toLowerCase();
    // API sends run_status "processing" while status is "pending" → show spinner
    if (runStatusLower === "processing") return true;

    const statusLower = (status || "").toLowerCase();
    if (!statusLower) return false;
    if (FAILED_STATUSES.includes(statusLower) || FAILED_STATUSES.includes(runStatusLower)) {
      return false;
    }
    return PROCESSING_STATUSES.includes(statusLower);
  }, []);

  const mapInvoiceToTableRow = useCallback((invoice, index, baseSerialNo = 0, agentNameOverride = null) => {
    const isProcessing = isProcessingStatus(invoice.status, invoice.run_status);
    return {
      id: invoice.id,
      serialNo: baseSerialNo + index + 1,
      documentId: invoice.invoice_details?.invoice_number || invoice.document_name || `DOC-${invoice.id}`,
      runStatus: formatRunStatus(invoice.status, invoice.run_status, isProcessing),
      entityName: invoice.entity_name || invoice.invoice_details?.vendor_name || "N/A",
      documentDate: formatDate(invoice.document_date || invoice.invoice_details?.invoice_date),
      agentName: invoice.agent_name || agentNameOverride || "N/A",
      source: invoice.source || DEFAULT_SOURCE,
      processedAt: formatDate(invoice.processed_at),
      exceptionStatus: invoice.exception_status || null,
      fullData: invoice,
      isProcessing: isProcessing,
      isAutomationOutput: false,
    };
  }, [isProcessingStatus]);

  const mapOutputToTableRow = useCallback(
    (output, index, baseSerialNo = 0, agentNameOverride = null) => {
      const statusLower = (output.status || "").toLowerCase();
      const isProcessing = isAutomationOutputProcessing(output.status);
      let runStatus = output.status || "—";
      if (statusLower === "completed") runStatus = STATUS_CONSTANTS.COMPLETED;
      else if (statusLower === "failed" || statusLower === "error") {
        runStatus = STATUS_CONSTANTS.FAILED;
      } else if (statusLower === "analysing_data") runStatus = "Analysing data";
      else if (statusLower === "pending") runStatus = STATUS_CONSTANTS.PENDING;
      else if (isProcessing) runStatus = STATUS_CONSTANTS.PROCESSING;

      return {
        id: output.id,
        serialNo: baseSerialNo + index + 1,
        documentId: output.output_table_name || `Output-${output.id}`,
        runStatus,
        entityName:
          output.automation_id != null ? `Automation #${output.automation_id}` : "N/A",
        documentDate: formatDate(output.created_at),
        agentName: agentNameOverride || (output.agent_id != null ? String(output.agent_id) : "N/A"),
        source: "Automation Output",
        processedAt: formatDate(output.updated_at ?? output.created_at),
        exceptionStatus: output.metadata?.error ?? null,
        fullData: output,
        isProcessing,
        isAutomationOutput: true,
      };
    },
    []
  );

  const fetchInvoiceProcessings = useCallback(async (agentId = null, page = 1, perPage = 10, options = {}) => {
    // Cancel any in-flight request before starting a new one
    if (invoiceProcessingsAbortRef.current) {
      invoiceProcessingsAbortRef.current.abort();
    }
    const controller = new AbortController();
    invoiceProcessingsAbortRef.current = controller;

    const { silent = false } = options || {};
    if (!silent) {
      setLoading(true);
    }
    try {
      const agentRecord =
        agents.find((a) => a.id === agentId) ||
        (selectedAgent?.id === agentId ? selectedAgent : null);
      const useOutputsApi =
        !agentId || !agentRecord || !isApAgent(agentRecord);
      const agentNameOverride = agentRecord?.name ?? null;

      if (useOutputsApi) {
        const {
          search = "",
          column = null,
          automationId = null,
        } = options || {};
        const outputParams = {
          page,
          per_page: perPage,
        };

        if (automationId) {
          outputParams.automation_id = automationId;
        } else if (agentId) {
          outputParams.agent_id = agentId;
        }

        if (column) {
          outputParams.column = column;
        }

        const trimmedSearch = typeof search === "string" ? search.trim() : "";
        if (trimmedSearch) {
          outputParams.search = trimmedSearch;
        }

        const { outputs, pagination: outputPagination, listColumns } =
          await fetchAutomationOutputs(outputParams, { signal: controller.signal });

        setOutputListColumns(listColumns);

        const resolveAgentName = (output) =>
          agentNameOverride ||
          agents.find((a) => a.id === output.agent_id)?.name ||
          (output.agent_id != null ? String(output.agent_id) : "N/A");

        const mappedData = listColumns
          ? outputs.map((item, index) =>
              mapOutputToDynamicTableRow(
                item,
                index,
                (page - 1) * perPage,
                listColumns,
                formatDate
              )
            )
          : outputs.map((item, index) =>
              mapOutputToTableRow(
                item,
                index,
                (page - 1) * perPage,
                resolveAgentName(item)
              )
            );
        setAllData(mappedData);

        if (outputPagination) {
          setTablePagination((prev) => {
            const newPagination = {
              current_page: outputPagination.current_page,
              per_page: outputPagination.per_page,
              total_pages: outputPagination.total_pages,
              total_count: outputPagination.total_count,
            };
            if (
              prev.current_page !== newPagination.current_page ||
              prev.per_page !== newPagination.per_page ||
              prev.total_pages !== newPagination.total_pages ||
              prev.total_count !== newPagination.total_count
            ) {
              return newPagination;
            }
            return prev;
          });
        }
        return;
      }

      const { search = "", column = null } = options || {};
      const params = {
        per_page: perPage,
        page: page,
      };

      if (agentId) {
        params.agent_id = agentId;
      }

      if (column) {
        params.column = column;
      }

      const trimmedSearch = typeof search === "string" ? search.trim() : "";
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      setOutputListColumns(null);

      const response = await axiosInstance.get("/api/v1/invoice_processings", {
        params,
        signal: controller.signal,
      });

      if (response.data?.invoice_processings) {
        const mappedData = response.data.invoice_processings.map((item, index) => {
          const isProcessing = isProcessingStatus(item.status, item.run_status);
          return {
            id: item.id,
            serialNo: (page - 1) * perPage + index + 1,
            documentId: item.invoice_details?.invoice_number || item.document_name || `DOC-${item.id}`,
            runStatus: formatRunStatus(item.status, item.run_status, isProcessing),
            entityName: item.entity_name || item.invoice_details?.vendor_name || "N/A",
            documentDate: formatDate(item.document_date || item.invoice_details?.invoice_date),
            agentName: item.agent_name || "N/A",
            source: item.source || DEFAULT_SOURCE,
            processedAt: formatDate(item.processed_at),
            exceptionStatus: item.exception_status || null,
            fullData: item,
            isProcessing: isProcessing,
          };
        });

        setAllData(mappedData);

        if (response.data.pagination) {
          setTablePagination((prev) => {
            const newPagination = {
              current_page: response.data.pagination.current_page,
              per_page: response.data.pagination.per_page,
              total_pages: response.data.pagination.total_pages,
              total_count: response.data.pagination.total_count,
            };
            if (
              prev.current_page !== newPagination.current_page ||
              prev.per_page !== newPagination.per_page ||
              prev.total_pages !== newPagination.total_pages ||
              prev.total_count !== newPagination.total_count
            ) {
              return newPagination;
            }
            return prev;
          });
        }
      }
    } catch (error) {
      // Ignore aborts triggered by new search/filter/pagination actions
      const isAborted =
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        error?.message?.toLowerCase?.().includes("canceled");
      if (!isAborted) {
        console.error("Error fetching invoice processings:", error);
        setAllData([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [agents, selectedAgent, isProcessingStatus, mapOutputToTableRow]);

  const rowIndicatesActiveProcessing = useCallback(
    (row) => {
      if (!row) return false;
      if (row.isRunAgentPlaceholder || row.isProcessing) return true;

      const outputStatus = row.fullData?.status ?? row.status;
      if (row.isAutomationOutput || row.fullData?.output_table_name != null) {
        return isAutomationOutputProcessing(outputStatus);
      }

      return isProcessingStatus(row.fullData?.status, row.fullData?.run_status);
    },
    [isProcessingStatus]
  );

  const hasActiveProcessingRows = useMemo(
    () => allData.some((row) => rowIndicatesActiveProcessing(row)),
    [allData, rowIndicatesActiveProcessing]
  );

  const listAgentId = selectedAutomation ? null : selectedAgent?.id ?? null;
  const listAutomationId = selectedAutomation?.id ?? null;

  useEffect(() => {
    fetchInvoiceProcessings(
      listAgentId,
      tablePagination.current_page,
      tablePagination.per_page,
      {
        search: debouncedSearchQuery,
        column: searchColumn,
        automationId: listAutomationId,
      }
    );
  }, [
    listAgentId,
    listAutomationId,
    tablePagination.current_page,
    tablePagination.per_page,
    debouncedSearchQuery,
    searchColumn,
    fetchInvoiceProcessings,
  ]);

  const isManualRunActive = runningAgentId != null || runningAutomationId != null;
  const fetchInvoiceProcessingsRef = useRef(fetchInvoiceProcessings);
  fetchInvoiceProcessingsRef.current = fetchInvoiceProcessings;
  const manualRunPollStartedRef = useRef(false);

  useEffect(() => {
    if (!isManualRunActive) {
      manualRunPollStartedRef.current = false;
    }
  }, [isManualRunActive]);

  useEffect(() => {
    if (!hasActiveProcessingRows && !isManualRunActive) return undefined;

    const poll = () => {
      fetchInvoiceProcessingsRef.current(
        listAgentId,
        tablePagination.current_page,
        tablePagination.per_page,
        {
          search: debouncedSearchQuery,
          column: searchColumn,
          automationId: listAutomationId,
          silent: true,
        }
      );
    };

    if (isManualRunActive && !manualRunPollStartedRef.current) {
      manualRunPollStartedRef.current = true;
      poll();
    }

    const intervalId = setInterval(poll, OUTPUT_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    hasActiveProcessingRows,
    isManualRunActive,
    listAgentId,
    listAutomationId,
    tablePagination.current_page,
    tablePagination.per_page,
    debouncedSearchQuery,
    searchColumn,
  ]);

  useEffect(() => {
    if (agentName) return;

    const automationIdParam = searchParams.get("automation_id");
    if (!automationIdParam) {
      if (selectedAutomation !== null) {
        setSelectedAutomation(null);
      }
      return;
    }

    if (idsMatch(selectedAutomation?.id, automationIdParam)) {
      return;
    }

    const found = automations.find((automation) =>
      idsMatch(automation.id, automationIdParam)
    );
    if (found) {
      setSelectedAutomation(found);
      return;
    }

    let cancelled = false;
    fetchAutomationById(automationIdParam)
      .then((automation) => {
        if (!cancelled && automation) {
          setSelectedAutomation(automation);
        }
      })
      .catch((error) => {
        console.error("Error fetching automation by id:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [agentName, searchParams, automations, selectedAutomation?.id]);

  useEffect(() => {
    if (agentName && agents.length > 0) {
      const agent = agents.find(
        a => normalizeAgentName(a.name) === agentName
      );

      if (agent) {
        // Only clear data if we're switching to a different agent
        if (selectedAgent?.id !== agent.id) {
          setTablePagination(prev => ({
            ...prev,
            current_page: 1,
          }));
          setAllData([]);
          setOutputListColumns(null);
        }
        setSelectedAgent(agent);
        setSelectedAutomation(null);
      } else {
        navigate("/skills");
      }
    } else if (!agentName && selectedAgent !== null) {
      // Only clear data if we had an agent selected and now we don't
      // Don't clear on initial load when selectedAgent is already null
      setTablePagination(prev => ({
        ...prev,
        current_page: 1,
      }));
      setAllData([]);
      setOutputListColumns(null);
      setSelectedAgent(null);
      setSelectedAutomation(null);
    }
  }, [agentName, agents, navigate, selectedAgent, normalizeAgentName]);


  useEffect(() => {
    if (selectedRow === null) return undefined;

    const handleClickOutside = (event) => {
      const actionMenu = event.target.closest('.action-menu');
      const threeDotMenu = event.target.closest('.three-dot-menu');
      const actionsCell = event.target.closest('.table-actions-cell');

      if (!actionMenu && !threeDotMenu && !actionsCell) {
        setSelectedRow(null);
      }
    };

    const timerId = window.setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedRow]);

  const handleAgentChunk = useCallback(async (chunk) => {
    const { status, payload } = chunk;
    const statusNorm = (status || "").toString();

    console.log("SSE Event received:", status, payload);

    // API event: InvoiceProcessings — payload.invoice_processings (array)
    if (statusNorm === EVENT_TYPES.INVOICE_PROCESSINGS) {
      const list = payload?.invoice_processings ?? payload?.invoices;
      if (Array.isArray(list) && list.length > 0) {
        setAllData((prevData) => {
          const nonPlaceholderData = removeRunPlaceholderRows(prevData);
          const existingIds = new Set(nonPlaceholderData.map(item => item.id));
          const newItems = list
            .map((ip, index) => mapInvoiceToTableRow(ip, index, nonPlaceholderData.length, selectedAgent?.name))
            .filter(item => !existingIds.has(item.id));
          return [...newItems, ...nonPlaceholderData];
        });
      }
      return;
    }

    // API event: finished — one invoice done; bind streamed fouray_response and PUT
    if (statusNorm.toLowerCase() === EVENT_TYPES.FINISHED) {
      const finishedInvoice = payload?.invoice_processing ?? payload?.invoice ?? payload;
      const fid = finishedInvoice?.id;
      if (fid != null) {
        setAllData((prevData) => {
          const nonPlaceholderData = removeRunPlaceholderRows(prevData);
          const idx = nonPlaceholderData.findIndex(item => item.id === fid);
          const row = mapInvoiceToTableRow(
            finishedInvoice,
            0,
            idx >= 0 ? idx : nonPlaceholderData.length,
            selectedAgent?.name
          );
          if (idx >= 0) {
            const next = [...nonPlaceholderData];
            next[idx] = { ...next[idx], ...row, serialNo: next[idx].serialNo };
            return next;
          }
          return [row, ...nonPlaceholderData];
        });

        // Prefer fouray from stream (Product.js parsed); then normalize + parse raw buffer; then merged payloads
        let fourayToSave = chunk.fouray_response;
        if (fourayToSave == null || typeof fourayToSave !== "object") {
          const rawBuf = chunk.fouray_raw_buffer;
          if (rawBuf && typeof rawBuf === "string" && rawBuf.trim().length > 0) {
            fourayToSave = tryParseFourayRawBuffer(rawBuf);
          }
        }

        if (fourayToSave == null || typeof fourayToSave !== "object") {
          const accumulated = streamedPayloadsByInvoiceIdRef.current[fid] || [];
          const merged = accumulated.reduce(
            (acc, p) =>
              deepMerge(
                acc,
                p?.invoice_processing && typeof p.invoice_processing === "object"
                  ? p.invoice_processing
                  : p?.invoice && typeof p.invoice === "object"
                    ? p.invoice
                    : p
              ),
            {}
          );
          if (merged.fouray_response != null || merged.response != null) {
            fourayToSave = merged.fouray_response ?? (merged.response != null && { response: merged.response });
          }
        }
        const hasFourayToSave = fourayToSave != null && typeof fourayToSave === "object";
        const fourayPayload = wrapFourayResponse(hasFourayToSave ? fourayToSave : {});

        if (!savedInvoiceIdsRef.current.has(fid)) {
          savedInvoiceIdsRef.current.add(fid);

          const updatePromise = (async () => {
            try {
              await updateInvoiceProcessing(fid, fourayPayload);
              const res = await getInvoiceProcessing(fid);
              const updated = res?.data;
              if (updated) {
                setAllData((prevData) =>
                  prevData.map((item) =>
                    item.id === fid
                      ? {
                        ...mapInvoiceToTableRow(
                          updated,
                          0,
                          prevData.findIndex((r) => r.id === fid),
                          selectedAgent?.name
                        ),
                        serialNo: item.serialNo,
                      }
                      : item
                  )
                );
              }
            } catch (err) {
              console.error("Save invoice processing failed:", err);
              savedInvoiceIdsRef.current.delete(fid);
            } finally {
              delete streamedPayloadsByInvoiceIdRef.current[fid];
              // Remove this promise from the set when done
              activeUpdatesRef.current.delete(updatePromise);
            }
          })();

          // Track this update operation
          activeUpdatesRef.current.add(updatePromise);
        } else {
          delete streamedPayloadsByInvoiceIdRef.current[fid];
        }
      }
      return;
    }

    // API event: finish (global) — clear refs and refetch list
    if (statusNorm.toLowerCase() === EVENT_TYPES.FINISH) {
      const list = payload?.invoice_processings ?? payload?.invoices;
      if (Array.isArray(list) && list.length > 0) {
        setAllData((prevData) => {
          const nonPlaceholderData = removeRunPlaceholderRows(prevData);
          const existingIds = new Set(nonPlaceholderData.map(item => item.id));
          const newItems = list
            .map((ip, index) => mapInvoiceToTableRow(ip, index, nonPlaceholderData.length, selectedAgent?.name))
            .filter(item => !existingIds.has(item.id));
          return [...newItems, ...nonPlaceholderData];
        });
      }
      // Wait for all pending updates to finish before clearing refs and fetching
      try {
        await Promise.all(activeUpdatesRef.current);
      } catch (error) {
        console.error("Error waiting for active updates:", error);
      }

      streamedPayloadsByInvoiceIdRef.current = {};
      savedInvoiceIdsRef.current.clear();
      activeUpdatesRef.current.clear(); // Safe to clear now

      if (selectedAgent?.id) {
        fetchInvoiceProcessings(selectedAgent.id, tablePagination.current_page, tablePagination.per_page);
      }
      return;
    }

    // API event: started, update, complete, completed, etc. — payload.invoice_processing (singular)
    const invoiceData = payload?.invoice_processing ?? payload?.invoice ?? payload;
    const invoiceId = invoiceData?.id;

    // Use status/run_status from response so table shows Processing, Analysing invoice, etc.
    const eventStatus = (payload?.status ?? status ?? "").toString().toLowerCase();
    const invoiceForRow =
      invoiceId != null
        ? {
          ...invoiceData,
          status: invoiceData?.status ?? eventStatus,
          run_status:
            invoiceData?.run_status ??
            (eventStatus === "completed" || eventStatus === "complete" ? "success" : undefined),
        }
        : invoiceData;

    if (invoiceId) {
      // Accumulate streamed chunks for this id; when streaming finishes we bind and save
      if (!streamedPayloadsByInvoiceIdRef.current[invoiceId]) {
        streamedPayloadsByInvoiceIdRef.current[invoiceId] = [];
      }
      streamedPayloadsByInvoiceIdRef.current[invoiceId].push(
        typeof payload === "object" ? { ...payload } : payload
      );
    }

    if (invoiceId) {
      setAllData((prevData) => {
        const nonPlaceholderData = removeRunPlaceholderRows(prevData);
        const existingIndex = nonPlaceholderData.findIndex(item => item.id === invoiceId);
        if (existingIndex >= 0) {
          const updated = [...nonPlaceholderData];
          const updatedItem = mapInvoiceToTableRow(invoiceForRow, 0, existingIndex, selectedAgent?.name);
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...updatedItem,
            serialNo: updated[existingIndex].serialNo,
          };
          return updated;
        } else {
          const newItem = mapInvoiceToTableRow(invoiceForRow, 0, nonPlaceholderData.length, selectedAgent?.name);
          return [newItem, ...nonPlaceholderData];
        }
      });
    }

    // When streaming finishes for this invoice: bind all chunks → fouray_response → save API → update row
    const isFileComplete =
      statusNorm.toLowerCase() === EVENT_TYPES.COMPLETED ||
      statusNorm.toLowerCase() === EVENT_TYPES.COMPLETE ||
      statusNorm.toLowerCase() === EVENT_TYPES.FINISHED;
    if (isFileComplete && invoiceId && !savedInvoiceIdsRef.current.has(invoiceId)) {
      savedInvoiceIdsRef.current.add(invoiceId);
      const accumulated = streamedPayloadsByInvoiceIdRef.current[invoiceId] || [];
      const merged = accumulated.reduce(
        (acc, p) =>
          deepMerge(
            acc,
            p?.invoice_processing && typeof p.invoice_processing === "object"
              ? p.invoice_processing
              : p?.invoice && typeof p.invoice === "object"
                ? p.invoice
                : p
          ),
        {}
      );
      const fourayResponse = wrapFourayResponse(
        merged.fouray_response ?? merged.response ?? merged
      );

      // Only call save API when we have fouray_response data (avoid overwriting with empty)
      const hasFourayData =
        merged.fouray_response != null ||
        (merged.response != null && typeof merged.response === "object");

      const updatePromise = (async () => {
        try {
          if (hasFourayData) {
            await updateInvoiceProcessing(invoiceId, fourayResponse);
          }
          const res = await getInvoiceProcessing(invoiceId);
          const updated = res?.data;
          if (updated) {
            setAllData((prevData) =>
              prevData.map((item) =>
                item.id === invoiceId
                  ? {
                    ...mapInvoiceToTableRow(
                      updated,
                      0,
                      prevData.findIndex((r) => r.id === invoiceId),
                      selectedAgent?.name
                    ),
                    serialNo: item.serialNo,
                  }
                  : item
              )
            );
          }
        } catch (err) {
          console.error("Save invoice processing failed:", err);
          savedInvoiceIdsRef.current.delete(invoiceId);
        } finally {
          delete streamedPayloadsByInvoiceIdRef.current[invoiceId];
          // Remove this promise from the set when done
          activeUpdatesRef.current.delete(updatePromise);
        }
      })();

      // Track this update operation
      activeUpdatesRef.current.add(updatePromise);
    }
  }, [mapInvoiceToTableRow, removeRunPlaceholderRows, selectedAgent, tablePagination, fetchInvoiceProcessings]);

  const handleStartAgentSubmit = useCallback(
    async (payload) => {
      if (!selectedAgent?.id) return;
      if (runningAgentId && idsMatch(runningAgentId, selectedAgent.id)) return;

      const agentId = selectedAgent.id;
      setShowStartRunModal(false);
      setRunningAgentId(agentId);

      const refreshList = () =>
        fetchInvoiceProcessings(agentId, tablePagination.current_page, tablePagination.per_page, {
          search: debouncedSearchQuery,
          column: searchColumn,
        });

      try {
        let outputTableName = "automation_outputs";
        try {
          const { automations } = await fetchAutomations({
            diy_agent_id: agentId,
            page: 1,
            per_page: 1,
          });
          outputTableName = resolveOutputTableNameFromAutomation(
            automations[0],
            outputTableName
          );
        } catch (error) {
          console.error("Failed to resolve output_table_name:", error);
        }

        const query =
          payload.query?.trim() || resolveAgentQuery(selectedAgent);

        await runStreaming(async (onChunk) =>
          executeManualAgent(
            agentId,
            generateSessionId(),
            query,
            onChunk,
            {},
            {
              agentName: selectedAgent.name || null,
              variables: payload.variables ?? "{}",
              filters: MANUAL_RUN_FILTERS,
              metadata: MANUAL_RUN_METADATA,
              outputTableName,
              source: "manual run",
              files: payload.files ?? [],
            }
          )
        );
        await refreshList();
      } catch (error) {
        console.error("Manual agent run failed:", error);
        window.alert(
          error?.message || "Manual run failed. Please try again."
        );
      } finally {
        setRunningAgentId(null);
      }
    },
    [
      selectedAgent,
      runningAgentId,
      runStreaming,
      fetchInvoiceProcessings,
      tablePagination.current_page,
      tablePagination.per_page,
      debouncedSearchQuery,
      searchColumn,
    ]
  );

  const handleStartAutomationSubmit = useCallback(
    async (payload) => {
      if (!selectedAutomation?.id) return;
      if (runningAutomationId && idsMatch(runningAutomationId, selectedAutomation.id)) {
        return;
      }

      const automationId = selectedAutomation.id;
      setShowStartRunModal(false);
      setRunningAutomationId(automationId);

      const refreshList = () =>
        fetchInvoiceProcessings(null, tablePagination.current_page, tablePagination.per_page, {
          search: debouncedSearchQuery,
          column: searchColumn,
          automationId,
        });

      try {
        const agentId = resolveAutomationAgentId(selectedAutomation);
        if (!agentId) {
          window.alert("This automation does not have an automation agent configured.");
          return;
        }

        const outputTableName = resolveOutputTableNameFromAutomation(selectedAutomation);
        const manualDefaults = buildManualAgentStreamOptions(selectedAutomation, {
          outputTableName,
        });
        const query =
          payload.query?.trim() || resolveAutomationQuery(selectedAutomation);

        await runStreaming(async (onChunk) =>
          executeManualAgent(
            agentId,
            generateSessionId(),
            query,
            onChunk,
            {},
            {
              variables: payload.variables ?? manualDefaults.variables,
              filters: MANUAL_RUN_FILTERS,
              metadata: MANUAL_RUN_METADATA,
              outputTableName,
              automationId,
              source: "manual run",
              files: payload.files ?? [],
            }
          )
        );
        await refreshList();
      } catch (error) {
        console.error("Automation action failed:", error);
        window.alert(
          error?.message || "Automation run failed. Please try again."
        );
      } finally {
        setRunningAutomationId(null);
      }
    },
    [
      selectedAutomation,
      runningAutomationId,
      runStreaming,
      fetchInvoiceProcessings,
      tablePagination.current_page,
      tablePagination.per_page,
      debouncedSearchQuery,
      searchColumn,
    ]
  );

  const handleRunModalSubmit = useCallback(
    async (payload) => {
      if (selectedAgent?.id) {
        await handleStartAgentSubmit(payload);
        return;
      }
      if (selectedAutomation?.id) {
        await handleStartAutomationSubmit(payload);
      }
    },
    [selectedAgent, selectedAutomation, handleStartAgentSubmit, handleStartAutomationSubmit]
  );

  const handleSearch = useCallback((value, columnKey) => {
    setSearchQuery(value || "");

    // Map UI column keys to API column param values
    let columnParam = null;
    switch (columnKey) {
      case "":
      case null:
      case undefined:
        columnParam = null;
        break;
      case "entity":
        columnParam = "entity_name";
        break;
      case "agent":
        columnParam = "agent_name";
        break;
      case "source":
        columnParam = "source";
        break;
      case "status":
        columnParam = "status";
        break;
      case "run_status":
        columnParam = "run_status";
        break;
      case "exception_status":
        columnParam = "exception_status";
        break;
      default:
        columnParam =
          outputListColumns?.includes(columnKey) ? columnKey : null;
    }

    setSearchColumn(columnParam);

    setTablePagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
  }, [outputListColumns]);

  const handleDelete = useCallback(async (rowData) => {
    if (!rowData || !rowData.id) {
      console.error("No valid row data or ID found for deletion");
      return;
    }

    setIsDeleting(true);
    try {
      const rowId = rowData.id;
      const useOutputsApi =
        rowData.isAutomationOutput ||
        !selectedAgent ||
        (selectedAgent && !isApAgent(selectedAgent));

      if (useOutputsApi) {
        await deleteAutomationOutput(rowId);
      } else {
        await axiosInstance.delete(`/api/v1/invoice_processings/${rowId}`);
      }

      setAllData((prevData) => prevData.filter((item) => item.id !== rowId));

      // Close the modal
      setShowDeleteModal(false);
      setRowToDelete(null);

      // Optionally refresh the data from the server
      await fetchInvoiceProcessings(listAgentId, tablePagination.current_page, tablePagination.per_page, {
        search: debouncedSearchQuery,
        column: searchColumn,
        automationId: listAutomationId,
      });
    } catch (error) {
      console.error("Error deleting invoice processing:", error);
      // You might want to show an error toast here
      alert("Failed to delete the invoice processing. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  }, [
    listAgentId,
    listAutomationId,
    selectedAgent,
    selectedAutomation,
    tablePagination,
    fetchInvoiceProcessings,
    debouncedSearchQuery,
    searchColumn,
  ]);

  const handleDuplicate = () => {
    // Duplicate functionality to be implemented
  };

  const handleDownload = useCallback(async (selectedRows) => {
    if (!selectedRows || selectedRows.length === 0) {
      return;
    }

    try {
      for (const row of selectedRows) {
        const invoiceData = row.fullData;
        let documentUrl = null;
        let documentName = null;

        // Extract document URL from attachment field
        if (invoiceData.attachment) {
          if (typeof invoiceData.attachment === 'string') {
            documentUrl = invoiceData.attachment;
          } else if (invoiceData.attachment.url) {
            documentUrl = invoiceData.attachment.url;
          } else if (invoiceData.attachment.filename) {
            documentUrl = invoiceData.attachment_url || null;
          }
        } else {
          documentUrl = invoiceData.attachment_url || null;
        }

        // Extract document name
        documentName = invoiceData.document_name ||
          invoiceData.documentName ||
          invoiceData.attachment?.filename ||
          invoiceData.invoice_details?.invoice_number ||
          `Document-${invoiceData.id}`;

        if (documentUrl) {
          // Fetch the file with authentication
          const token = localStorage.getItem('token');
          const response = await fetch(documentUrl, {
            headers: token ? { 'Authorization': token } : {},
          });

          if (!response.ok) {
            console.error(`Failed to download document for record ${invoiceData.id}`);
            continue;
          }

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = documentName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          console.warn(`No document URL found for record ${invoiceData.id}`);
        }
      }
    } catch (error) {
      console.error("Error downloading documents:", error);
    }
  }, []);

  const handleDownloadCSVClick = useCallback((selectedRows) => {
    const rowsArray = selectedRows || [];
    if (rowsArray.length === 0 && !downloadSelectAll) {
      return;
    }
    setPendingDownloadRows(rowsArray);
    setShowDownloadModal(true);
  }, [downloadSelectAll]);

  const handleDownloadCSV = useCallback(async (selectedRows, type = "normal", fileType = "csv") => {
    const rowsArray = selectedRows || [];
    if (!downloadSelectAll && rowsArray.length === 0) {
      return;
    }

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (!downloadSelectAll) {
        // Extract IDs from selected rows only when not selecting all
        const invoiceIds = rowsArray.map(row => row.id || row.fullData?.id).filter(Boolean);

        if (invoiceIds.length === 0) {
          console.error("No valid invoice IDs found in selected rows");
          return;
        }

        invoiceIds.forEach(id => {
          params.append('ids[]', id);
        });
      } else {
        // For "select all" mode, explicitly pass an empty ids[] param
        params.append('ids[]', '');
      }
      params.append('type', type);

      // When selecting all across pages, also send current search + column
      if (downloadSelectAll) {
        if (searchColumn) {
          params.append('column', searchColumn);
        }
        const trimmedSearch = typeof searchQuery === "string" ? searchQuery.trim() : "";
        if (trimmedSearch) {
          params.append('search', trimmedSearch);
        }
      }

      const response = await axiosInstance.get(`/api/v1/invoice_processings/download?${params.toString()}`, {
        responseType: 'blob', // Important for CSV download
      });

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_processings_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  }, [downloadSelectAll, searchColumn, searchQuery]);

  const handleGlobalSelectAllChange = useCallback((isSelected) => {
    setDownloadSelectAll(isSelected);
  }, []);

  const handleMenuOption = useCallback((action, rowData) => {
    setSelectedRow(null);

    if (action === "edit" || action === "preview") {
      const invoiceProcessingId = rowData.id;

      if (!invoiceProcessingId) {
        console.error("No invoice processing ID found in row data:", rowData);
        return;
      }

      let backLink = "/skills";
      if (selectedAgent) {
        const normalizedName = normalizeAgentName(selectedAgent.name);
        backLink = `/skills/${normalizedName}`;
      }

      const params = new URLSearchParams({
        back: backLink,
      });

      const isAutomationOutputRow =
        rowData.isAutomationOutput ||
        !selectedAgent ||
        (selectedAgent && !isApAgent(selectedAgent));

      if (isAutomationOutputRow) {
        params.set("resource", "automation_outputs");
        params.set("hideDocumentViewer", "1");
      } else if (selectedAgent && !agentHasUploadEnabled(selectedAgent)) {
        params.set("hideDocumentViewer", "1");
      } else {
        const rowAgentId = rowData.fullData?.agent_id ?? rowData.agent_id;
        const rowAgent = agents.find((agent) => agent.id === rowAgentId);
        if (rowAgent && !agentHasUploadEnabled(rowAgent)) {
          params.set("hideDocumentViewer", "1");
        }
      }

      navigate(`/playground/${invoiceProcessingId}?${params.toString()}`);
    } else if (action === "delete") {
      setRowToDelete(rowData);
      setShowDeleteModal(true);
    }
  }, [selectedAgent, agents, normalizeAgentName, navigate]);

  const handleAgentClick = useCallback((agent) => {
    setSelectedAutomation(null);
    const normalizedName = normalizeAgentName(agent.name);
    navigate(`/skills/${normalizedName}`);
  }, [normalizeAgentName, navigate]);

  const handleAutomationClick = useCallback((automation) => {
    if (idsMatch(selectedAutomation?.id, automation.id)) {
      setSelectedAutomation(null);
      navigate("/skills");
    } else {
      setSelectedAutomation(automation);
      navigate(`/skills?automation_id=${automation.id}`);
    }

    setTablePagination((prev) => ({
      ...prev,
      current_page: 1,
    }));
    setAllData([]);
    setOutputListColumns(null);
  }, [selectedAutomation?.id, navigate]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  }, [pagination.total_pages]);

  const renderOutputStatusCell = useCallback((status, row) => {
      const statusLower = String(status || "").toLowerCase();
      const isProcessing =
        row.original?.isProcessing ||
        isAutomationOutputProcessing(status) ||
        ["pending", "running", "processing"].includes(statusLower);

    if (isProcessing) {
      return (
        <div style={{ display: "flex", alignItems: "center", minHeight: "24px" }}>
          <span
            className="status-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
              lineHeight: 1.2,
            }}
          >
            <div
              style={{
                width: "14px",
                height: "14px",
                border: `2px solid ${STATUS_COLORS.PROCESSING_BG}`,
                borderTop: `2px solid ${STATUS_COLORS.PROCESSING}`,
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <span style={{ color: STATUS_COLORS.PROCESSING }}>{PROCESSING_TEXT}</span>
          </span>
        </div>
      );
    }

    let statusClass = "status-completed";
    let dotColor = STATUS_COLORS.COMPLETED;
    let label = formatTableCellText(status);

    if (["failed", "error"].includes(statusLower)) {
      statusClass = "status-failed";
      dotColor = STATUS_COLORS.FAILED;
      label = STATUS_CONSTANTS.FAILED;
    } else if (["completed", "success"].includes(statusLower)) {
      label = STATUS_CONSTANTS.COMPLETED;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", minHeight: "24px" }}>
        <span
          className={`status-badge ${statusClass}`}
          style={{ whiteSpace: "nowrap", lineHeight: 1.2 }}
        >
          <span style={{ color: dotColor, marginRight: "5px" }}>•</span>
          {label}
        </span>
      </div>
    );
  }, [formatTableCellText]);

  const dynamicOutputColumns = useMemo(() => {
    if (!outputListColumns?.length) return null;

    const dataColumns = outputListColumns.map((key) => ({
      id: key,
      accessorFn: (row) => row[key],
      header: humanizeOutputColumnKey(key),
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("70%");
        }

        if (key === "status") {
          return renderOutputStatusCell(getValue(), row);
        }

        const value = formatTableCellText(getValue());
        return <span title={String(value)}>{value}</span>;
      },
    }));

    return [
      {
        id: "selection",
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={() => table.toggleAllPageRowsSelected()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={() => row.toggleSelected()}
          />
        ),
        size: "5%",
      },
      ...dataColumns,
      {
        header: "Actions",
        cell: ({ row }) =>
          row.original?.isRunAgentPlaceholder ? (
            <span style={{ color: "#9ca3af" }}>—</span>
          ) : (
            <TableActionsCell
              row={row.original}
              rowKey={row.original?.id ?? row.id}
              selectedRowKey={selectedRow}
              onToggle={(key) => setSelectedRow(selectedRow === key ? null : key)}
              onMenuAction={(action, rowData) => {
                handleMenuOption(action, rowData);
                setSelectedRow(null);
              }}
            />
          ),
      },
    ];
  }, [outputListColumns, selectedRow, renderOutputStatusCell, handleMenuOption, formatTableCellText]);

  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
    },
    {
      accessorKey: "serialNo",
      header: "Serial No.",
    },
    {
      accessorKey: "documentId",
      header: "Document ID",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("75%");
        }
        const value = formatTableCellText(getValue());
        return <span title={String(value)}>{value}</span>;
      },
    },
    {
      accessorKey: "runStatus",
      header: "Run status",
      cell: ({ getValue, row }) => {
        const status = getValue();
        const isProcessing = row.original.isProcessing || status === STATUS_CONSTANTS.PROCESSING;

        if (isProcessing) {
          return (
            <div style={{ display: "flex", alignItems: "center", minHeight: "24px" }}>
              <span className="status-badge" style={{ display: "inline-flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    border: `2px solid ${STATUS_COLORS.PROCESSING_BG}`,
                    borderTop: `2px solid ${STATUS_COLORS.PROCESSING}`,
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite"
                  }}
                />
                <span style={{ color: STATUS_COLORS.PROCESSING }}>{PROCESSING_TEXT}</span>
              </span>
            </div>
          );
        }

        let statusClass = "status-completed";
        let dotColor = STATUS_COLORS.COMPLETED;

        if (status === STATUS_CONSTANTS.REVIEW_EXCEPTIONS) {
          statusClass = "status-review";
          dotColor = STATUS_COLORS.REVIEW_EXCEPTIONS;
        } else if (status === STATUS_CONSTANTS.FAILED) {
          statusClass = "status-failed";
          dotColor = STATUS_COLORS.FAILED;
        }

        return (
          <div style={{ display: "flex", alignItems: "center", minHeight: "24px" }}>
            <span className={`status-badge ${statusClass}`} style={{ whiteSpace: "nowrap", lineHeight: 1.2 }}>
              <span style={{ color: dotColor, marginRight: "5px" }}>•</span>
              {formatTableCellText(status)}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "entityName",
      header: "Entity Name",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("85%");
        }
        const value = formatTableCellText(getValue());
        return <span title={String(value)}>{value}</span>;
      },
    },
    {
      accessorKey: "documentDate",
      header: "Document Date",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("60%");
        }
        return getValue() || "—";
      },
    },
    {
      accessorKey: "agentName",
      header: "Agent Name",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("70%");
        }
        const value = formatTableCellText(getValue());
        return <span title={String(value)}>{value}</span>;
      },
    },
    {
      accessorKey: "source",
      header: "Source",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("65%");
        }
        const value = formatTableCellText(getValue());
        return <span title={String(value)}>{value}</span>;
      },
    },
    {
      accessorKey: "processedAt",
      header: "Processed At",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("55%");
        }
        const value = getValue() || "—";
        const rawProcessedAt = row.original?.fullData?.processed_at;
        const readableTime = rawProcessedAt
          ? new Date(rawProcessedAt).toLocaleString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : String(value);
        return <span title={readableTime}>{value}</span>;
      },
    },
    {
      accessorKey: "exceptionStatus",
      header: "Exception Status",
      cell: ({ getValue, row }) => {
        if (row.original?.isRunAgentPlaceholder) {
          return renderLoadingSkeleton("50%");
        }
        const status = getValue();
        if (!status) return <span style={{ color: "#9ca3af" }}>—</span>;

        const statusColors = {
          approve: { color: "#10b981", bg: "#d1fae5" },
          reject: { color: "#ef4444", bg: "#fee2e2" },
          hold: { color: "#f59e0b", bg: "#fef3c7" },
        };

        const statusConfig = statusColors[status.toLowerCase()] || { color: "#6b7280", bg: "#f3f4f6" };

        return (
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 500,
              backgroundColor: statusConfig.bg,
              color: statusConfig.color,
              textTransform: "capitalize",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        row.original?.isRunAgentPlaceholder ? (
          <span style={{ color: "#9ca3af" }}>—</span>
        ) : (
        <TableActionsCell
          row={row.original}
          rowKey={row.original?.id ?? row.id}
          selectedRowKey={selectedRow}
          onToggle={(key) => setSelectedRow(selectedRow === key ? null : key)}
          onMenuAction={(action, rowData) => {
            handleMenuOption(action, rowData);
            setSelectedRow(null);
          }}
        />
        )
      ),
    },
  ];

  const useDynamicOutputColumns =
    (!selectedAgent || !isApAgent(selectedAgent)) &&
    Array.isArray(outputListColumns) &&
    outputListColumns.length > 0 &&
    dynamicOutputColumns;

  const visibleColumns = useDynamicOutputColumns
    ? dynamicOutputColumns
    : selectedAgent || selectedAutomation
      ? columns.filter(
          (column) =>
            column.accessorKey !== "agentName" &&
            column.accessorKey !== "source"
        )
      : columns;

  const displayedAutomations = selectedAutomation
    ? [selectedAutomation]
    : selectedAgent
      ? []
      : automations;

  const displayedAgents = selectedAgent
    ? [selectedAgent]
    : selectedAutomation
      ? []
      : agents;

  const showUploadInvoice = selectedAgent ? agentHasUploadEnabled(selectedAgent) : false;
  const showRunButton =
    Boolean(selectedAutomation) ||
    (selectedAgent && !agentHasUploadEnabled(selectedAgent));
  const cardsLoading = loadingAgents || loadingAutomations;
  const isStartingSelectedAutomation =
    runningAutomationId != null &&
    selectedAutomation?.id != null &&
    idsMatch(runningAutomationId, selectedAutomation.id);
  const isRunningSelectedAgent =
    runningAgentId != null &&
    selectedAgent?.id != null &&
    idsMatch(runningAgentId, selectedAgent.id);
  const isStartingSelectedRun =
    isStartingSelectedAutomation || isRunningSelectedAgent;

  return (
    <div className="skills-page">
      <Breadcrumb />
      <div className="skills-page-header d-flex justify-content-between align-items-center mx-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div className="title-label">
            {selectedAgent
              ? selectedAgent.name
              : selectedAutomation
                ? selectedAutomation.workflow_name ?? selectedAutomation.name ?? `Automation #${selectedAutomation.id}`
                : "Agents"}
          </div>

        
        </div>

        {(selectedAutomation || selectedAgent) && (
          <div className="skills-page-actions">
            {showRunButton && (
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "14px",
                }}
                onClick={() => setShowStartRunModal(true)}
                disabled={isStartingSelectedRun}
              >
                <RefreshCw
                  size={16}
                  className={isStartingSelectedRun ? "animate-spin" : ""}
                />
                {isStartingSelectedRun ? "Running..." : "Run"}
              </button>
            )}

            {selectedAgent && showUploadInvoice && (
              <button
                className="btn d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "14px",
                }}
                onClick={() => setShowUploadModal(true)}
              >
                <ArrowUp size={16} />
                Upload Invoice
              </button>
            )}
          </div>
        )}
      </div>

      <div
        className="skills-container"
        style={{ display: "flex", gap: "16px", marginTop: "5px", marginBottom: "30px", flexWrap: "wrap" }}
      >
        {cardsLoading ? (
          Array.from({ length: 14 }).map((_, index) => (
            <AgentCardSkeleton key={`skeleton-${index}`} />
          ))
        ) : (
          <>
            {displayedAutomations.map((automation, index) => (
              <SkillCard
                key={automation?.id || automation?.workflow_name || `automation-${index}`}
                icon={talkingToAIIcon}
                title={
                  automation.workflow_name ??
                  automation.name ??
                  `Automation #${automation.id}`
                }
                onClick={() => handleAutomationClick(automation)}
              />
            ))}
            {displayedAgents.length > 0 ? (
              displayedAgents.map((agent, index) => (
                <SkillCard
                  key={agent?.id || agent?.name || index}
                  icon={agent.icon}
                  title={agent.name}
                  onClick={() => handleAgentClick(agent)}
                />
              ))
            ) : (
              !displayedAutomations.length && (
                <div className="text-center py-5 w-100">
                  <p>No agents found</p>
                </div>
              )
            )}
          </>
        )}
      </div>

      {!selectedAgent && !selectedAutomation && pagination.total_count > 0 && (
        <div className="d-flex justify-content-end align-items-center mx-3 mb-4" style={{ marginTop: "20px" }}>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <div className="d-flex gap-1">
              <button
                className="btn btn-outline-secondary"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1 || cardsLoading}
                style={{ padding: "4px 12px", fontSize: "14px" }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages || cardsLoading}
                style={{ padding: "4px 12px", fontSize: "14px" }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`skills-table-section mt-3${
          useDynamicOutputColumns ? " skills-table-section--dynamic-output" : ""
        }`}
      >
        <ReusableTable
          data={allData}
          columns={visibleColumns}
          onSearch={handleSearch}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onDownload={handleDownload}
          onDownloadCSV={handleDownloadCSVClick}
          gridView={false}
          manualPagination={true}
          pageCount={tablePagination.total_pages}
          totalCount={tablePagination.total_count}
          initialPageIndex={tablePagination.current_page - 1}
          initialPageSize={tablePagination.per_page}
          getRowId={(row) => row.id}
          isLoading={loading}
          onSelectAllAcrossPagesChange={handleGlobalSelectAllChange}
          onPaginationChange={(newPagination) => {
            const newPage = newPagination.pageIndex + 1;
            const newPageSize = newPagination.pageSize;

            if (newPage !== tablePagination.current_page || newPageSize !== tablePagination.per_page) {
              setTablePagination((prev) => ({
                ...prev,
                current_page: newPage,
                per_page: newPageSize,
              }));
            }
          }}
        />
      </div>

      <StartAutomationModal
        show={showStartRunModal}
        onHide={() => setShowStartRunModal(false)}
        automation={selectedAutomation}
        agent={selectedAgent}
        isRunning={isStartingSelectedRun}
        onSubmit={handleRunModalSubmit}
      />

      <DocumentModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        agent={selectedAgent}
        initialTags=""
        companyKnowledgeDocs={EMPTY_ARRAY}
        groupedDocuments={EMPTY_ARRAY}
        onSuccess={async () => {
          try {
            await Promise.all(activeUpdatesRef.current);
          } catch (error) {
            console.error("Error waiting for active updates:", error);
          }

          if (selectedAgent?.id) {
            fetchInvoiceProcessings(selectedAgent.id, tablePagination.current_page, tablePagination.per_page, {
              search: debouncedSearchQuery,
              column: searchColumn,
            });
          }
        }}
        onAgentChunk={(chunk) => {
          handleAgentChunk(chunk);
        }}
      />

      <DownloadFormatModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setPendingDownloadRows([]);
        }}
        onDownload={handleDownloadCSV}
        selectedRows={pendingDownloadRows}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDelete}
        rowData={rowToDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Skill;
